import { useEffect, useState } from 'react';
import { Monitor, Plug } from 'lucide-react';
import type { DestinationConnectionSummary } from '../shared/desktop-api';
import { TooltipButton } from './TooltipButton';
import {
  alertingConnections,
  EXPORT_DESTINATION_CHANGED_EVENT,
  readConnectionUse,
} from './connection-use';
import './computer-door.css';

/** Sections of the computer's settings window a caller may open directly. */
export type ComputerSection =
  'performance' | 'storage' | 'connections' | 'appearance' | 'cluster' | 'license' | 'updates';
export const OPEN_COMPUTER_EVENT = 'capturepoint-open-computer';
export const CONNECTIONS_CHANGED_EVENT = 'capturepoint-connections-changed';
/** A new connection was just saved on this computer; detail is its connection id. */
export const CONNECTION_ADDED_EVENT = 'capturepoint-connection-added';

/** Opens the computer's settings window. Without a section it returns to the last one used. */
export function openComputerSettings(section?: ComputerSection) {
  window.dispatchEvent(
    new CustomEvent(OPEN_COMPUTER_EVENT, { detail: section ? { section } : {} }),
  );
}

/**
 * The one door to settings that apply to every job on this computer. It opens
 * the settings window itself: a menu in between would only repeat the window's
 * own list of sections, and a list of values read as status, not choices.
 */
export function ComputerDoorButton({
  onOpen = () => openComputerSettings(),
}: {
  onOpen?: () => void;
}) {
  return (
    <TooltipButton
      type="button"
      className="computer-door-button"
      aria-label="This computer"
      tooltip="Settings · Ctrl+,"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onOpen}
    >
      <Monitor size={17} aria-hidden="true" />
      <span>This computer</span>
    </TooltipButton>
  );
}

/**
 * Status, not a setting: it exists only while a saved connection that some
 * job's export uses is failing, and a click goes straight to Connections,
 * where it can be fixed. A failing connection no job uses stays visible in
 * Connections but does not nag here.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function ConnectionAlert() {
  const [failed, setFailed] = useState<DestinationConnectionSummary[]>([]);
  useEffect(() => {
    let live = true,
      attempt = 0;
    const read = () => {
      const current = ++attempt;
      const api = window.capturePointDesktop;
      const reading = api?.listDestinationConnections?.();
      if (!api || !reading) return;
      reading
        .then(async (list) => {
          const failing = list.filter((item) => item.lastFailureCode);
          // Only ask which jobs export where when something is failing.
          const scoped = failing.length
            ? alertingConnections(failing, await readConnectionUse(api))
            : [];
          if (live && current === attempt) setFailed(scoped);
        })
        // Settings reports a failed read; the tray only speaks when it knows.
        .catch(() => undefined);
    };
    read();
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, read);
    window.addEventListener(EXPORT_DESTINATION_CHANGED_EVENT, read);
    window.addEventListener('focus', read);
    return () => {
      live = false;
      window.removeEventListener(CONNECTIONS_CHANGED_EVENT, read);
      window.removeEventListener(EXPORT_DESTINATION_CHANGED_EVENT, read);
      window.removeEventListener('focus', read);
    };
  }, []);
  if (!failed.length) return null;
  const problem =
    failed.length === 1
      ? `${failed[0].displayName} can't connect`
      : `${failed.length} connections can't connect`;
  return (
    <TooltipButton
      type="button"
      className="computer-connection-alert"
      aria-label={`${problem}. Open Connections`}
      tooltip="Connection problem"
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => openComputerSettings('connections')}
    >
      <Plug size={17} aria-hidden="true" />
    </TooltipButton>
  );
}
